import chalk from 'chalk';

interface LogStyle {
  background?: string;
  color?: string;
  fontWeight?: string;
  borderRadius?: string;
  padding?: string;
}

type ConsoleLogType = 'debug' | 'error' | 'info' | 'log' | 'trace' | 'warn';
type LogType = 'error' | 'warn' | 'info' | 'success' | 'trace';

interface StyledLogPart {
  text: string;
  style?: LogStyle;
}

class LogHelper {
  private defaultStyles: Record<LogType, LogStyle>;
  private isNodeEnvironment: boolean;

  constructor() {
    this.defaultStyles = {
      error: { background: 'red', color: 'white', fontWeight: 'bold' },
      warn: { background: 'yellow', color: 'black', fontWeight: 'bold' },
      info: { background: 'orange', color: 'black', fontWeight: 'bold' },
      success: { background: 'green', color: 'white', fontWeight: 'bold' },
      trace: { background: 'purple', color: 'white', fontWeight: 'bold' },
    };
    this.isNodeEnvironment
      = typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
  }

  log = console.log.bind(console);

  error = console.error.bind(console);

  warn = console.warn.bind(console);

  info = console.info.bind(console);

  trace = console.trace.bind(console);

  debug = console.debug.bind(console);

  assert = console.assert.bind(console);

  dir(obj: any, options?: any): void {
    console.dir(obj, options);
  }

  dirxml(...args: any[]): void {
    console.dirxml(...args);
  }

  group(...args: any[]): void {
    console.group(...args);
  }

  groupCollapsed(...args: any[]): void {
    console.groupCollapsed(...args);
  }

  groupEnd(): void {
    console.groupEnd();
  }

  time(label?: string): void {
    console.time(label);
  }

  timeEnd(label?: string): void {
    console.timeEnd(label);
  }

  timeLog(label?: string, ...args: any[]): void {
    console.timeLog(label, ...args);
  }

  table(data: any, columns?: string[]): void {
    console.table(data, columns);
  }

  clear(): void {
    console.clear();
  }

  count(label?: string): void {
    console.count(label);
  }

  countReset(label?: string): void {
    console.countReset(label);
  }

  profile(label?: string): void {
    console.profile(label);
  }

  profileEnd(label?: string): void {
    console.profileEnd(label);
  }

  colorError(message: string, style?: LogStyle, ...args: any[]): void {
    this.logMessage('error', message, style, ...args);
  }

  colorWarn(message: string, style?: LogStyle, ...args: any[]): void {
    this.logMessage('warn', message, style, ...args);
  }

  colorInfo(message: string, style?: LogStyle, ...args: any[]): void {
    this.logMessage('info', message, style, ...args);
  }

  colorSuccess(message: string, style?: LogStyle, ...args: any[]): void {
    this.logMessage('success', message, style, ...args);
  }

  colorTrace(message: string, style?: LogStyle, ...args: any[]): void {
    this.logMessage('trace', message, style, ...args);
  }

  styledLog(parts: StyledLogPart[], ...args: any[]): void {
    this.logStyledMessage('log', parts, ...args);
  }

  styledDebug(parts: StyledLogPart[], ...args: any[]): void {
    this.logStyledMessage('debug', parts, ...args);
  }

  styledInfo(parts: StyledLogPart[], ...args: any[]): void {
    this.logStyledMessage('info', parts, ...args);
  }

  styledWarn(parts: StyledLogPart[], ...args: any[]): void {
    this.logStyledMessage('warn', parts, ...args);
  }

  styledError(parts: StyledLogPart[], ...args: any[]): void {
    this.logStyledMessage('error', parts, ...args);
  }

  styledTrace(parts: StyledLogPart[], ...args: any[]): void {
    this.logStyledMessage('trace', parts, ...args);
  }

  private logMessage(type: LogType, message: string, style?: LogStyle, ...args: any[]): void {
    if (this.isNodeEnvironment) {
      this.logInNode(type, message, style, ...args);
    } else {
      this.logInBrowser(type, message, style, ...args);
    }
  }

  private logStyledMessage(type: ConsoleLogType, parts: StyledLogPart[], ...args: any[]): void {
    if (this.isNodeEnvironment) {
      console[type](parts.map(part => part.text).join(''), ...args);
      return;
    }

    const message = parts.map(part => `%c${part.text}`).join('');
    const styles = parts.map(part => this.toCssStyle(part.style));
    console[type](message, ...styles, ...args);
  }

  private toCssStyle(style?: LogStyle): string {
    if (!style) {
      return '';
    }

    return [
      style.background ? `background: ${style.background}` : '',
      style.color ? `color: ${style.color}` : '',
      style.fontWeight ? `font-weight: ${style.fontWeight}` : '',
      style.borderRadius ? `border-radius: ${style.borderRadius}` : '',
      style.padding ? `padding: ${style.padding}` : '',
    ].filter(Boolean).join('; ');
  }

  private logInNode(type: LogType, message: string, style?: LogStyle, ...args: any[]) {
    const finalStyle = { ...this.defaultStyles[type], ...style };
    const { background, color, fontWeight } = finalStyle;
    let styledType = type.toUpperCase();

    if (background) {
      styledType = chalk.bgHex(background)(styledType);
    }
    if (color) {
      styledType = chalk.hex(color)(styledType);
    }
    if (fontWeight === 'bold') {
      styledType = chalk.bold(styledType);
    }

    switch (type) {
      case 'error':
        console.error(`${styledType}:`, message, ...args);
        break;
      case 'warn':
        console.warn(`${styledType}:`, message, ...args);
        break;
      case 'info':
        console.info(`${styledType}:`, message, ...args);
        break;
      case 'success':
        console.log(`${styledType}:`, message, ...args);
        break;
      case 'trace':
        console.trace(`${styledType}:`, message, ...args);
        break;
    }
  }

  private logInBrowser(type: LogType, message: string, style?: LogStyle, ...args: any[]) {
    const finalStyle = { ...this.defaultStyles[type], ...style };
    const cssStyle = this.toCssStyle(finalStyle);

    switch (type) {
      case 'error':
        console.error(`%c ${type.toUpperCase()} `, cssStyle, message, ...args);
        break;
      case 'warn':
        console.warn(`%c ${type.toUpperCase()} `, cssStyle, message, ...args);
        break;
      case 'info':
        console.info(`%c ${type.toUpperCase()} `, cssStyle, message, ...args);
        break;
      case 'success':
        console.log(`%c ${type.toUpperCase()} `, cssStyle, message, ...args);
        break;
      case 'trace':
        console.trace(`%c ${type.toUpperCase()} `, cssStyle, message, ...args);
        break;
    }
  }
}

export const logHelper = new LogHelper();
